import { Drawer, List, ListItem, Tabs } from '@silverfort/sf-components';
import { useEffect, useState } from 'react';
import { EVENT_SEVERITIES, SILVERFORT_RECOMMENDED_EVENTS } from './EmailConsts';
import ChipBank from '../../../sf-components/ChipBank/ChipBank';

const TABS = ['Default', 'Custom'];

const NUMERIC_CHARS_ONLY_REGEX = /^\d+$/;

const validateEventIdInList = (eventId, list) => {
    if (!NUMERIC_CHARS_ONLY_REGEX.test(eventId)) return 'Non-numeric event ids found';
    if (eventId.toString().length !== 4) return 'Event ids must be 4 digits long';
    if (list.eventIds.filter((id) => id === eventId).length > 1) return 'Duplicate event ids found';
};

const DefaultTab = ({ selections, setSelections }) => {
    const calcSelectAllStatus = (selections) => {
        if (selections.eventIds.length === SILVERFORT_RECOMMENDED_EVENTS.length) return 'checked';
        if (selections.eventIds.length > 0) return 'indeterminate';
        return 'unchecked';
    };

    const selectAllStatus = calcSelectAllStatus(selections);

    const checkAll = () => {
        const eventIds = [];
        if (selectAllStatus !== 'checked') {
            eventIds.push(...SILVERFORT_RECOMMENDED_EVENTS.map((event) => event.value));
        }
        setSelections({ eventIds });
    };

    const checkOne = (value) => {
        const eventIds = selections.eventIds;
        if (eventIds.includes(value)) {
            setSelections({ eventIds: eventIds.filter((selection) => selection !== value) });
        } else {
            setSelections({ eventIds: [...eventIds, value] });
        }
    };

    return (
        <div className={'w-full h-[calc(100%-48px)] flex flex-col'}>
            <div className={'text-sf-gray-500 font-sf font-medium text-xs my-4'}>
                Selection ({selections.eventIds.length})
            </div>
            <List className={'w-full flex-1 overflow-y-hidden hover:overflow-y-auto'}>
                <ListItem
                    title={'Select all'}
                    multiSelect={true}
                    selected={selectAllStatus === 'checked'}
                    indeterminate={selectAllStatus === 'indeterminate'}
                    onCheck={checkAll}
                    className={'mt-1 font-medium'}
                />
                {SILVERFORT_RECOMMENDED_EVENTS.map((event, i) => (
                    <ListItem
                        key={i}
                        title={event.title}
                        value={event.value}
                        subtitle={event.subtitle}
                        multiSelect={true}
                        selected={selections.eventIds.includes(event.value)}
                        onCheck={checkOne}
                        className={'mt-1'}
                    />
                ))}
            </List>
        </div>
    );
};

const CustomTab = ({ selections, setSelections, error }) => {
    const checkOne = (value) => {
        const severity = selections.severity;
        if (severity.includes(value)) {
            setSelections({ ...selections, severity: severity.filter((selection) => selection !== value) });
        } else {
            setSelections({ ...selections, severity: [...severity, value] });
        }
    };

    const addEventIds = (eventIds) => {
        setSelections({ ...selections, eventIds: [...selections.eventIds, ...eventIds.map((eventId) => +eventId)] });
    };

    const removeAllEventIds = () => {
        setSelections({ ...selections, eventIds: [] });
    };

    const removeEventId = (eventId, index) => {
        const eventIds = [...selections.eventIds];
        eventIds.splice(index, 1);
        setSelections({ ...selections, eventIds });
    };

    const validateEventIdInput = (eventId) => {
        if (!NUMERIC_CHARS_ONLY_REGEX.test(eventId)) return 'Use numeric characters only';
        if (eventId.length !== 4) return 'Event ID must be 4 digits long';
        if (selections.eventIds.includes(+eventId)) return 'Event ID already exists';
    };

    return (
        <>
            <div>
                <div className={'text-13 font-sf font-medium mt-4'}>Event severity</div>
                <List className={'w-full h-full pl-0'}>
                    {EVENT_SEVERITIES.map((event, i) => (
                        <ListItem
                            key={i}
                            title={event.title}
                            value={event.value}
                            multiSelect={true}
                            selected={selections.severity.includes(event.value)}
                            onCheck={checkOne}
                            className={'mt-1'}
                        />
                    ))}
                </List>
            </div>
            <div className={'h-full'}>
                <div className={'text-13 font-sf font-medium my-4'}>Event ID</div>
                <ChipBank
                    validateInput={validateEventIdInput}
                    validateListItem={(eventId) => validateEventIdInList(eventId, selections)}
                    data={selections.eventIds}
                    addData={addEventIds}
                    removeAllData={removeAllEventIds}
                    removeData={removeEventId}
                    placeholder={'Enter ID number'}
                    listTitle={'Events'}
                    listClassName={'w-full min-h-40 max-h-96'}
                />
                <span className="font-sf-primary text-10 font-normal text-sf-negative-500">{error}</span>
            </div>
        </>
    );
};

const NotificationsEventDrawer = ({ data, open, setOpen, onSave, title, description }) => {
    const [selectedTab, setSelectedTab] = useState(TABS[0]);
    const [defaultSelections, setDefaultSelections] = useState(data?.default || { eventIds: [] });
    const [customSelections, setCustomSelections] = useState(data?.custom || { eventIds: [], severity: [] });
    const [error, setError] = useState(true);
    useEffect(() => setError(), [customSelections]);

    const closeDrawer = () => {
        setOpen(null);
        setSelectedTab(TABS[0]);
        setError();
    };

    const isValid = () => {
        const customEventsErrors = customSelections.eventIds
            .map((eventId) => validateEventIdInList(eventId, customSelections))
            .filter((error) => !!error);
        setError(customEventsErrors?.[0]);
        return !customEventsErrors.length;
    };

    const saveData = () => {
        if (isValid()) {
            onSave({ default: defaultSelections, custom: customSelections });
            closeDrawer();
        }
    };

    return (
        <Drawer
            variant={'persistent'}
            open={!!open}
            anchor="right"
            footerActionTitle="Apply"
            footerActionProps={{ disabled: !!error }}
            footerCloseTitle="Cancel"
            headerTitle={title}
            headerDescription={description}
            onAction={saveData}
            onClose={closeDrawer}>
            <div className={'flex flex-col w-[512px] h-full overflow-hidden'}>
                <Tabs
                    onClick={setSelectedTab}
                    tabs={TABS.map((tab) => ({ name: tab, value: tab }))}
                    selectedTab={selectedTab}></Tabs>
                {selectedTab === 'Default' && (
                    <DefaultTab selections={defaultSelections} setSelections={setDefaultSelections} />
                )}
                {selectedTab === 'Custom' && (
                    <CustomTab selections={customSelections} setSelections={setCustomSelections} error={error} />
                )}
            </div>
        </Drawer>
    );
};

export default NotificationsEventDrawer;
